import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useDebounce } from 'react-use'
import { useTranslation } from 'react-i18next'
import {
  Check,
  Keyboard,
  Loader2,
  Mouse,
  MousePointer2,
  MousePointerClick,
  MoreVertical,
  Redo2,
  Undo2,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import Card from 'app/frontend/base/components/Card'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import ViewerPresenceButton from 'app/frontend/base/components/ViewerPresenceButton'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { useRaidPlan } from 'app/frontend/base/config/queries'
import { abilityTypes, AbilityType } from 'app/frontend/types/custom/Abilities'
import RaidPlan, { RaidPlanTimeline } from 'app/frontend/types/custom/RaidPlan'
import {
  RaidPlanOwner,
  createRaidPlan,
  deleteRaidPlan,
  setDefaultRaidPlan,
  updateRaidPlan,
} from 'app/frontend/base/api/raid_plans'
import DefaultRaidPlanToggle from './DefaultRaidPlanToggle'
import { useIsDefaultRaidPlan } from './defaultRaidPlan'
import useRaidPlanChannel from '../hooks/useRaidPlanChannel'
import useTeamPlanComputedData from '../hooks/useTeamPlanComputedData'
import TeamPlanTimeline from './TeamPlanTimeline'
import ImportFromWarcraftLogsDialog from './ImportFromWarcraftLogsDialog'
import ImportFromNoteDialog from './ImportFromNoteDialog'
import CopyFromPlanDialog from './CopyFromPlanDialog'
import CreateNoteDialog from './CreateNoteDialog'
import PlanExportStringDialog from './PlanExportStringDialog'
import PlanImportStringDialog from './PlanImportStringDialog'
import {
  countRaidPlanContent,
  encodeRaidPlanTransfer,
  loadLinkedVisualPlansForExport,
  parseRaidPlanImport,
  resolveImportedRaidPlanTimeline,
  type RaidPlanImportSuccess,
} from './raidPlanTransfer'
import {
  CursorState,
  TeamPlanTimelineProvider,
  TimelineZoom,
} from './TeamPlanTimelineContext'
import TeamPlanCompositionSummary from './TeamPlanCompositionSummary'
import PlanReadonlyAccessAlert from './PlanReadonlyAccessAlert'
import RaidPlanMessageContent, {
  RaidPlanMessageControl,
} from './RaidPlanMessage'
import { hasNotesContent } from 'app/frontend/base/components/NotesEditor'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import {
  getViewerColorClass,
  getViewerInitials,
  getViewerLabel,
} from './viewerPresence'

const cloneTimeline = (timeline: RaidPlanTimeline): RaidPlanTimeline =>
  JSON.parse(JSON.stringify(timeline)) as RaidPlanTimeline

const sameTimeline = (a: RaidPlanTimeline, b: RaidPlanTimeline) =>
  JSON.stringify(a) === JSON.stringify(b)

type TeamPlanEditorContentProps = {
  owner: RaidPlanOwner
  basePath: string
  planListPathPrefix: string
  rosterMemberships: wowaudit.TeamMembership[]
  canEdit: boolean
  showViewers: boolean
  showRosterImport: boolean
}

const TeamPlanEditorContent = ({
  owner,
  basePath,
  planListPathPrefix,
  rosterMemberships,
  canEdit,
  showViewers,
  showRosterImport,
}: TeamPlanEditorContentProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isDefaultRaidPlan = useIsDefaultRaidPlan()
  const { planId } = useParams()

  const { data: raidPlan } = useRaidPlan(owner, Number(planId))

  const [name, setName] = useState('')
  const [timeline, setTimeline] = useState<RaidPlanTimeline>({ actors: [] })
  const [selectedType, setSelectedType] = useState<AbilityType>(() => {
    if (typeof window === 'undefined') return 'key_cooldown'
    const hashType = window.location.hash.replace('#', '')
    if (abilityTypes.includes(hashType as AbilityType)) {
      return hashType as AbilityType
    }
    return 'key_cooldown'
  })
  const [zoom, setZoom] = useState<TimelineZoom | null>(null)
  const [dragFrameMax, setDragFrameMax] = useState<number | null>(null)
  const [initializedPlanId, setInitializedPlanId] = useState<number>()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const [isImportFromNoteDialogOpen, setIsImportFromNoteDialogOpen] =
    useState(false)
  const [isCopyFromPlanDialogOpen, setIsCopyFromPlanDialogOpen] =
    useState(false)
  const [isExportStringDialogOpen, setIsExportStringDialogOpen] =
    useState(false)
  const [exportString, setExportString] = useState('')
  const [isExportStringLoading, setIsExportStringLoading] = useState(false)
  const [isImportStringDialogOpen, setIsImportStringDialogOpen] =
    useState(false)
  const [isCreateNoteDialogOpen, setIsCreateNoteDialogOpen] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false)
  const [isEditingMessage, setIsEditingMessage] = useState(false)

  const historyRef = useRef<RaidPlanTimeline[]>([])
  const historyIndexRef = useRef(-1)
  const initializedPlanIdRef = useRef<number>()
  const dirtyRef = useRef(false)
  const timelineRef = useRef<RaidPlanTimeline>({ actors: [] })
  const savedNameRef = useRef('')
  const timelineRootRef = useRef<HTMLDivElement>(null)
  const cursorContextRef = useRef(`tab:${selectedType}`)

  useEffect(() => {
    timelineRef.current = timeline
  }, [timeline])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const nextHash = `#${selectedType}`
    if (window.location.hash === nextHash) return
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}${nextHash}`,
    )
  }, [selectedType])

  const setDirtyState = useCallback((next: boolean) => {
    dirtyRef.current = next
    setIsDirty(next)
  }, [])

  const updateTimeline = useCallback(
    (updater: (next: RaidPlanTimeline) => void, pushHistory = true) => {
      if (!canEdit) return
      const next = cloneTimeline(timelineRef.current)
      updater(next)
      setTimeline(next)
      setDirtyState(true)
      if (!pushHistory) return
      historyRef.current = historyRef.current.slice(
        0,
        historyIndexRef.current + 1,
      )
      historyRef.current.push(next)
      historyIndexRef.current = historyRef.current.length - 1
    },
    [canEdit, setDirtyState],
  )

  useEffect(() => {
    if (!raidPlan) return
    if (initializedPlanIdRef.current === raidPlan.id) return
    initializedPlanIdRef.current = raidPlan.id
    setName(raidPlan.name)
    savedNameRef.current = raidPlan.name
    setTimeline(raidPlan.timeline)
    setZoom(null)
    historyRef.current = [raidPlan.timeline]
    historyIndexRef.current = 0
    setDirtyState(false)
    setInitializedPlanId(raidPlan.id)
  }, [raidPlan, setDirtyState])

  const updateMutation = useMutation({
    mutationFn: async ({
      nextName,
      nextTimeline,
      nextMessage,
    }: {
      nextName: string
      nextTimeline: RaidPlanTimeline
      nextMessage?: string
    }) => {
      if (!raidPlan) throw new Error('Plan missing')
      return await updateRaidPlan(owner, raidPlan.id, {
        name: nextName,
        timeline: nextTimeline,
        ...(nextMessage !== undefined ? { message: nextMessage } : {}),
      })
    },
    onSuccess: async (updated) => {
      await queryClient.setQueryData(
        ['raidPlan', { owner, planId: updated.id }],
        updated,
      )
      await queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
      await queryClient.invalidateQueries({ queryKey: ['userRaidPlans'] })
    },
    onError: () => {
      setDirtyState(true)
    },
  })

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      if (!raidPlan) throw new Error('Plan missing')
      const baseName = name.trim() || 'New plan'
      return await createRaidPlan(owner, {
        encounter_id: raidPlan.encounterId,
        difficulty: raidPlan.difficulty,
        name: `${baseName} (copy)`,
        message: raidPlan.message,
        timeline,
      })
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
      await queryClient.invalidateQueries({ queryKey: ['userRaidPlans'] })
      await queryClient.invalidateQueries({ queryKey: ['team'] })
      navigate(`${basePath}/${created.id}`)
    },
  })

  const setDefaultMutation = useMutation({
    mutationFn: async () => {
      if (!raidPlan || owner.ownerType !== 'team')
        throw new Error('Plan missing')
      return await setDefaultRaidPlan(owner.ownerId, raidPlan.id)
    },
    onSuccess: (updatedTeam) => {
      queryClient.setQueryData(
        ['team', { path: updatedTeam.path }],
        updatedTeam,
      )
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!raidPlan) throw new Error('Plan missing')
      return await deleteRaidPlan(owner, raidPlan.id)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
      await queryClient.invalidateQueries({ queryKey: ['userRaidPlans'] })
      navigate(basePath)
    },
  })

  const updateMessageMutation = useMutation({
    mutationFn: async (message: string) => {
      if (!raidPlan) throw new Error('Plan missing')
      return await updateRaidPlan(owner, raidPlan.id, { message })
    },
    onSuccess: async (updated) => {
      await queryClient.setQueryData(
        ['raidPlan', { owner, planId: updated.id }],
        updated,
      )
      await queryClient.invalidateQueries({ queryKey: ['raidPlans'] })
    },
  })

  const applyPlanFromSource = useCallback(
    (source: { timeline: RaidPlanTimeline; message?: string }) => {
      const sourceTimeline = cloneTimeline(source.timeline)
      updateTimeline((next) => {
        Object.keys(next).forEach((key) => {
          delete (next as Record<string, unknown>)[key]
        })
        Object.assign(next, sourceTimeline)
      })
      setIsEditingMessage(false)
      updateMutation.mutate({
        nextName: savedNameRef.current,
        nextTimeline: sourceTimeline,
        nextMessage: source.message ?? '',
      })
    },
    [updateMutation, updateTimeline],
  )

  useEffect(() => {
    if (!isExportStringDialogOpen || !raidPlan) {
      setExportString('')
      setIsExportStringLoading(false)
      return
    }

    let cancelled = false
    setIsExportStringLoading(true)
    setExportString('')

    const loadExportString = async () => {
      try {
        const visualPlans = await loadLinkedVisualPlansForExport(
          owner,
          timeline,
          () => {
            toast.error(
              'A linked visual plan could not be included in the export',
            )
          },
        )
        if (cancelled) return
        setExportString(
          encodeRaidPlanTransfer({
            timeline,
            message: raidPlan.message,
            visualPlans,
          }),
        )
      } catch {
        if (cancelled) return
        toast.error('Could not include linked visual plans in the export')
        setExportString(
          encodeRaidPlanTransfer({
            timeline,
            message: raidPlan.message,
            visualPlans: [],
          }),
        )
      } finally {
        if (!cancelled) setIsExportStringLoading(false)
      }
    }

    void loadExportString()
    return () => {
      cancelled = true
    }
  }, [isExportStringDialogOpen, owner, raidPlan, timeline])

  useDebounce(
    () => {
      if (!raidPlan || !dirtyRef.current) return
      setDirtyState(false)
      updateMutation.mutate({
        nextName: savedNameRef.current,
        nextTimeline: timeline,
      })
    },
    700,
    [timeline, raidPlan?.id, setDirtyState],
  )

  const handleRemotePlanUpdate = useCallback(
    (updated: RaidPlan) => {
      if (!raidPlan || updated.id !== raidPlan.id) return
      if (
        updated.name === savedNameRef.current &&
        sameTimeline(updated.timeline, timelineRef.current)
      ) {
        return
      }
      savedNameRef.current = updated.name
      setName(updated.name)
      setTimeline(updated.timeline)
      setZoom(null)
      historyRef.current = [updated.timeline]
      historyIndexRef.current = 0
      setDirtyState(false)
    },
    [raidPlan, setDirtyState],
  )

  const handlePlanDeleted = useCallback(
    (deletedPlanId: number) => {
      if (!raidPlan || deletedPlanId !== raidPlan.id) return
      toast.error('This plan has been deleted')
      navigate(basePath)
    },
    [basePath, navigate, raidPlan],
  )

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current <= 0) return
    historyIndexRef.current -= 1
    setTimeline(historyRef.current[historyIndexRef.current])
    setDirtyState(true)
  }, [setDirtyState])

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return
    historyIndexRef.current += 1
    setTimeline(historyRef.current[historyIndexRef.current])
    setDirtyState(true)
  }, [setDirtyState])

  const { viewers, cursors, requiresReload, sendCursor } = useRaidPlanChannel({
    planId: raidPlan?.id,
    onPlanUpdated: handleRemotePlanUpdate,
    onPlanDeleted: handlePlanDeleted,
  })
  const cursorContext = `tab:${selectedType}`

  useEffect(() => {
    if (cursorContextRef.current === cursorContext) return
    const previousContext = cursorContextRef.current
    cursorContextRef.current = cursorContext
    sendCursor(null, null, previousContext)
  }, [cursorContext, sendCursor])
  const setAssignmentDialogOpen = useCallback(
    (open: boolean) => {
      setIsAssignmentDialogOpen(open)
      if (open) {
        sendCursor(null, null, cursorContextRef.current)
      }
    },
    [sendCursor],
  )
  const sendCursorWithDialogGuard = useCallback(
    (x: number | null, y: number | null, context: string) => {
      if (isAssignmentDialogOpen) return
      sendCursor(x, y, context)
    },
    [isAssignmentDialogOpen, sendCursor],
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (!zoom) return
        if (event.defaultPrevented) return
        const target = event.target
        if (
          target instanceof HTMLElement &&
          (target.isContentEditable ||
            target.closest('input, textarea, select, [contenteditable="true"]'))
        ) {
          return
        }
        if (
          document.querySelector(
            '[role="dialog"], [role="alertdialog"], [data-radix-popper-content-wrapper]',
          )
        ) {
          return
        }
        event.preventDefault()
        setZoom(null)
        return
      }

      if (!canEdit) return
      const isUndo =
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === 'z' &&
        !event.shiftKey
      const isRedo =
        ((event.metaKey || event.ctrlKey) &&
          event.key.toLowerCase() === 'z' &&
          event.shiftKey) ||
        (event.ctrlKey && !event.metaKey && event.key.toLowerCase() === 'y')

      if (isUndo) {
        event.preventDefault()
        handleUndo()
        return
      }
      if (isRedo) {
        event.preventDefault()
        handleRedo()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [canEdit, handleRedo, handleUndo, zoom])

  const computed = useTeamPlanComputedData({
    raidPlan,
    timeline,
    selectedType,
    t,
  })

  // While any phase boundary is being dragged we freeze the timeline width to a
  // frame captured at drag start (baseline length plus the dragged phase's
  // growth headroom). This keeps every row and the minute labels perfectly
  // still: shortening reveals empty space to the right and lengthening fills it,
  // then the frame snaps to the new length on release.
  const effectiveComputed = useMemo(() => {
    if (dragFrameMax === null || dragFrameMax <= computed.maxTime) {
      return computed
    }
    return { ...computed, maxTime: dragFrameMax }
  }, [computed, dragFrameMax])

  const timelineContextValue = useMemo(() => {
    if (!raidPlan) return undefined
    return {
      canEdit,
      owner,
      basePath,
      planListPathPrefix,
      rosterMemberships,
      raidPlan,
      timeline,
      isInitializing: initializedPlanId !== raidPlan.id,
      selectedType,
      setSelectedType,
      zoom,
      setZoom,
      setDragFrameMax,
      computed: effectiveComputed,
      timelineRootRef,
      viewers,
      cursors: cursors as CursorState[],
      showRosterImport,
      cursorContext,
      setAssignmentDialogOpen,
      sendCursor: sendCursorWithDialogGuard,
      updateTimeline,
      applyPlanFromSource,
    }
  }, [
    canEdit,
    owner,
    basePath,
    planListPathPrefix,
    rosterMemberships,
    raidPlan,
    timeline,
    initializedPlanId,
    selectedType,
    setSelectedType,
    zoom,
    effectiveComputed,
    viewers,
    cursors,
    showRosterImport,
    cursorContext,
    setAssignmentDialogOpen,
    sendCursorWithDialogGuard,
    updateTimeline,
    applyPlanFromSource,
  ])

  if (!raidPlan || !computed.encounter || !timelineContextValue) return null

  const handleNameBlur = () => {
    const nextName = name.trim() === '' ? 'New plan' : name
    if (nextName !== name) setName(nextName)
    if (nextName === savedNameRef.current) return
    savedNameRef.current = nextName
    updateMutation.mutate({
      nextName,
      nextTimeline: timelineRef.current,
    })
  }

  const handleDuplicatePlan = () => {
    duplicateMutation.mutate()
  }

  const handleDeletePlan = () => {
    setIsDeleteDialogOpen(true)
  }

  const handleImportFromWarcraftLogs = () => {
    setIsImportDialogOpen(true)
  }

  const handleImportFromNote = () => {
    setIsImportFromNoteDialogOpen(true)
  }

  const handleDeletePlanConfirm = () => {
    setIsDeleteDialogOpen(false)
    deleteMutation.mutate()
  }

  const canUndo = historyIndexRef.current > 0
  const canRedo = historyIndexRef.current < historyRef.current.length - 1

  return (
    <div className='min-w-0 space-y-2'>
      <div className='flex items-start gap-3 justify-between'>
        <h2 className='flex items-center gap-3 min-w-0 flex-wrap justify-start'>
          {computed.encounter.thumbnailUrl ? (
            <img
              src={computed.encounter.thumbnailUrl}
              className='w-10 h-10 rounded-md border border-border shrink-0'
            />
          ) : (
            <div className='w-10 h-10 rounded-md border border-border bg-muted shrink-0' />
          )}
          <span className='truncate min-w-24'>{computed.encounter.name}</span>
          <Badge variant='outline'>
            {difficultiesLabels[raidPlan.difficulty]}
          </Badge>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className='inline-flex'>
                <Badge variant={'outline'}>
                  {isDirty || updateMutation.isPending ? (
                    <Loader2 className='h-3.5 w-3.5 animate-spin' />
                  ) : (
                    <Check className='h-3.5 w-3.5 text-success-foreground' />
                  )}
                </Badge>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {isDirty || updateMutation.isPending
                ? 'Saving changes...'
                : 'Up to date'}
            </TooltipContent>
          </Tooltip>
        </h2>

        <div className='flex items-center gap-2 flex-wrap justify-end'>
          {showViewers ? (
            viewers.length > 3 ? (
              <ViewerPresenceButton viewers={viewers} />
            ) : (
              viewers.map((viewer, viewerIndex) => (
                <Tooltip key={viewer.key}>
                  <TooltipTrigger asChild>
                    <div
                      className={`h-8 w-8 rounded-full text-xs font-semibold cursor-default text-white flex items-center justify-center ${getViewerColorClass(
                        viewerIndex,
                      )}`}
                    >
                      {getViewerInitials(viewer.label)}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {getViewerLabel(viewer.label)}
                  </TooltipContent>
                </Tooltip>
              ))
            )
          ) : null}
          {canEdit ? (
            <>
              <Button
                variant='outline'
                size='icon'
                onClick={handleUndo}
                disabled={!canUndo}
              >
                <Undo2 className='h-4 w-4' />
              </Button>
              <Button
                variant='outline'
                size='icon'
                onClick={handleRedo}
                disabled={!canRedo}
              >
                <Redo2 className='h-4 w-4' />
              </Button>
            </>
          ) : null}
          <Button
            onClick={() => setIsCreateNoteDialogOpen(true)}
            variant='outline'
          >
            {t('dashboard.team.plans.create-note.open')}
          </Button>
          <Button variant='outline' onClick={() => navigate(basePath)}>
            {t('shared.back-to-list')}
          </Button>

          {canEdit ? (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='outline'
                  size='icon'
                  disabled={
                    duplicateMutation.isPending || deleteMutation.isPending
                  }
                >
                  <MoreVertical className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem
                  onClick={handleDuplicatePlan}
                  disabled={duplicateMutation.isPending}
                >
                  Duplicate plan
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsExportStringDialogOpen(true)}
                  disabled={
                    duplicateMutation.isPending || deleteMutation.isPending
                  }
                >
                  {t('dashboard.team.plans.export')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsImportStringDialogOpen(true)}
                  disabled={
                    duplicateMutation.isPending || deleteMutation.isPending
                  }
                >
                  {t('dashboard.team.plans.import')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleImportFromWarcraftLogs}
                  disabled={
                    duplicateMutation.isPending || deleteMutation.isPending
                  }
                >
                  Import from Warcraft Logs
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleImportFromNote}
                  disabled={
                    duplicateMutation.isPending || deleteMutation.isPending
                  }
                >
                  Import from note
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsCopyFromPlanDialogOpen(true)}
                  disabled={
                    duplicateMutation.isPending || deleteMutation.isPending
                  }
                >
                  {t('dashboard.team.plans.onboarding.copy-from-another-plan')}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleDeletePlan}
                  className='text-destructive-foreground'
                  disabled={deleteMutation.isPending}
                >
                  Delete plan
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      {!canEdit ? <PlanReadonlyAccessAlert /> : null}

      {(() => {
        const messageActive =
          isEditingMessage || hasNotesContent(raidPlan.message)

        const nameInput = canEdit ? (
          <Input
            className='text-xl font-semibold field-sizing-content min-w-50 w-fit'
            value={name}
            onChange={(event) => {
              setName(event.target.value)
            }}
            onBlur={handleNameBlur}
            placeholder={'New plan'}
          />
        ) : (
          <p className='text-xl font-semibold w-fit'>{name || 'New plan'}</p>
        )

        const isDefault =
          owner.ownerType === 'team' && isDefaultRaidPlan(raidPlan)
        const showDefaultCard =
          owner.ownerType === 'team' && (canEdit || isDefault)

        const nameRow = (
          <div className='flex items-center gap-2 w-fit'>
            {nameInput}
            {showDefaultCard ? (
              <DefaultRaidPlanToggle
                isDefault={isDefault}
                canEdit={canEdit}
                disabled={setDefaultMutation.isPending}
                onToggle={() => setDefaultMutation.mutate()}
              />
            ) : null}
          </div>
        )

        const compositionRow = (
          <div className='flex items-center gap-1'>
            {!messageActive ? (
              <RaidPlanMessageControl
                raidPlan={raidPlan}
                canEdit={canEdit}
                isEditing={isEditingMessage}
                onStartEdit={() => setIsEditingMessage(true)}
              />
            ) : null}
            <TeamPlanCompositionSummary timeline={timeline} />

            <HoverCard openDelay={120}>
              <HoverCardTrigger asChild>
                <Button
                  type='button'
                  variant={'outline'}
                  size='custom'
                  className='h-7.5 py-0.5 px-2'
                >
                  <Keyboard className='h-4 w-4' />
                </Button>
              </HoverCardTrigger>
              <HoverCardContent side='bottom' align='end' className='w-fit p-3'>
                <div className='text-sm font-medium'>Timeline controls</div>
                <div className='mt-2 space-y-1.5 text-xs text-muted-foreground'>
                  <div className='flex items-center gap-2'>
                    <MousePointerClick className='h-3.5 w-3.5 text-foreground' />
                    <span>Left click to add or edit cast</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <MousePointer2 className='h-3.5 w-3.5 text-foreground' />
                    <span>Click and drag cast to move it</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <MousePointer2 className='h-3.5 w-3.5 text-foreground' />
                    <span>Alt + click and drag cast to copy it</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <MousePointerClick className='h-3.5 w-3.5 text-foreground' />
                    <span>Click a phase or its label to zoom in</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <MousePointerClick className='h-3.5 w-3.5 text-foreground' />
                    <span>
                      Click the zoomed timeline, right-click, or press Escape to
                      zoom out
                    </span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <MousePointer2 className='h-3.5 w-3.5 text-foreground' />
                    <span>Click and drag to zoom to a time range</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Mouse className='h-3.5 w-3.5 text-foreground' />
                    <span>Right click to remove cast</span>
                  </div>
                  <div className='flex items-center gap-2'>
                    <Mouse className='h-3.5 w-3.5 text-foreground' />
                    <span>Shift + right click to remove all casts in row</span>
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>
        )

        if (messageActive) {
          return (
            <>
              <div className='flex items-end justify-between gap-2'>
                {nameRow}
                {canEdit && !isEditingMessage ? (
                  <Button
                    variant='outline'
                    size='xs'
                    onClick={() => setIsEditingMessage(true)}
                  >
                    Edit
                  </Button>
                ) : null}
              </div>
              <RaidPlanMessageContent
                raidPlan={raidPlan}
                isEditing={isEditingMessage}
                onStopEdit={() => setIsEditingMessage(false)}
                onSave={async (message) => {
                  await updateMessageMutation.mutateAsync(message)
                  setIsEditingMessage(false)
                }}
              />
              <div className='flex justify-end'>{compositionRow}</div>
            </>
          )
        }

        return (
          <div className='flex items-end gap-2 flex-wrap justify-between'>
            {nameRow}
            {compositionRow}
          </div>
        )
      })()}

      <Card className='min-w-0 overflow-x-auto p-4 md:overflow-x-visible'>
        <div className='min-w-[40rem]'>
          <TeamPlanTimelineProvider value={timelineContextValue}>
            <TeamPlanTimeline />
          </TeamPlanTimelineProvider>
        </div>
      </Card>
      <ConfirmDialog
        open={isDeleteDialogOpen}
        descriptionKey='dashboard.team.plans.delete-confirm'
        onCancel={() => setIsDeleteDialogOpen(false)}
        onContinue={handleDeletePlanConfirm}
        variant='destructive'
      />
      <PlanExportStringDialog
        open={isExportStringDialogOpen}
        onOpenChange={setIsExportStringDialogOpen}
        exportString={exportString}
        isLoading={isExportStringLoading}
        description={t('dashboard.team.plans.export-description')}
      />
      <PlanImportStringDialog<RaidPlanImportSuccess>
        open={isImportStringDialogOpen}
        onOpenChange={setIsImportStringDialogOpen}
        hasExistingContent={countRaidPlanContent(timeline) > 0}
        description={t('dashboard.team.plans.import-description')}
        invalidMessage={t('dashboard.team.plans.import-invalid')}
        overwriteConfirmMessage={t(
          'dashboard.team.plans.import-overwrite-confirm',
        )}
        parse={(input) => {
          const result = parseRaidPlanImport(input)
          if (!result.ok) return { ok: false }
          return {
            ok: true,
            value: result,
            summary: t('dashboard.team.plans.import-summary', {
              actorCount: result.actorCount,
              castCount: result.castCount,
              assignmentCount: result.assignmentCount,
              visualCount: result.visualCount,
            }),
          }
        }}
        onImport={async (imported) => {
          const remappedTimeline = await resolveImportedRaidPlanTimeline({
            owner,
            destOwner: {
              ownerType: raidPlan.ownerType,
              ownerId: raidPlan.ownerId,
            },
            encounterId: raidPlan.encounterId,
            difficulty: raidPlan.difficulty,
            timeline: imported.timeline,
            visualPlans: imported.visualPlans,
          })
          await queryClient.invalidateQueries({ queryKey: ['visualPlans'] })
          await queryClient.invalidateQueries({ queryKey: ['visualPlan'] })
          applyPlanFromSource({
            timeline: remappedTimeline,
            message: imported.message ?? '',
          })
        }}
      />
      <ImportFromWarcraftLogsDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        encounterId={raidPlan.encounterId}
        owner={owner}
        difficulty={raidPlan.difficulty}
        timeline={timeline}
        onImported={(importedTimeline) => {
          const nextTimeline = cloneTimeline(importedTimeline)
          setTimeline(nextTimeline)
          historyRef.current = [nextTimeline]
          historyIndexRef.current = 0
          setDirtyState(true)
        }}
      />
      <CopyFromPlanDialog
        open={isCopyFromPlanDialogOpen}
        onOpenChange={setIsCopyFromPlanDialogOpen}
        owner={owner}
        planListPathPrefix={planListPathPrefix}
        currentPlan={raidPlan}
        timeline={timeline}
        onApply={applyPlanFromSource}
        onImportFromExportString={() => setIsImportStringDialogOpen(true)}
      />
      <ImportFromNoteDialog
        open={isImportFromNoteDialogOpen}
        onOpenChange={setIsImportFromNoteDialogOpen}
        raidPlan={raidPlan}
        encounterWclId={computed.encounter.wclId}
        timeline={timeline}
        rosterMemberships={rosterMemberships}
        onImported={(importedTimeline) => {
          const nextTimeline = cloneTimeline(importedTimeline)
          setTimeline(nextTimeline)
          historyRef.current = [nextTimeline]
          historyIndexRef.current = 0
          setDirtyState(false)
        }}
      />
      <CreateNoteDialog
        key={raidPlan.id}
        owner={owner}
        raidPlan={raidPlan}
        planListPathPrefix={planListPathPrefix}
        open={isCreateNoteDialogOpen}
        onOpenChange={setIsCreateNoteDialogOpen}
        onActorPreferredCharacterChange={
          canEdit
            ? (actorUuid, characterId) =>
                updateTimeline((next) => {
                  const actor = next.actors.find(
                    (entry) => entry.uuid === actorUuid,
                  )
                  if (actor) actor.preferred_character_id = characterId
                })
            : undefined
        }
      />
      <AlertDialog open={requiresReload}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Connection lost</AlertDialogTitle>
            <AlertDialogDescription>
              Live sync is unavailable and could not reconnect. Please reload
              before continuing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => window.location.reload()}>
              Reload
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

const TeamPlanEditor = () => {
  const { team } = useTeam()
  const { accessLevel } = useAuth()

  return (
    <TeamPlanEditorContent
      owner={{ ownerType: 'team', ownerId: team.id }}
      basePath={`${team.path}/events/plans`}
      planListPathPrefix={team.path}
      rosterMemberships={team.members ?? []}
      canEdit={accessLevel === 'admin'}
      showViewers={true}
      showRosterImport={true}
    />
  )
}

export { TeamPlanEditorContent }
export default TeamPlanEditor
